import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

export default function GuidePage() {
  return (
    <>
      <Navbar />
      <main>
        {/* Article Hero */}
        <section className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
          <div className="absolute inset-0 mx-3 md:mx-6 my-3 md:my-6 rounded-2xl md:rounded-3xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Clean%20minimal%20workspace%20desk%20with%20laptop%20showing%20spreadsheet%20interface%20surrounded%20by%20fashion%20magazines%20and%20sneakers%20on%20warm%20cream%20background%20soft%20natural%20daylight%20editorial%20photography%20airy%20aesthetic%20calm%20neutral%20tones&width=1600&height=900&seq=article-hero-gtbuy-guide&orientation=landscape"
              alt="GTBuy Sheet Ultimate Guide 2026"
              title="GTBuy Sheet Ultimate Guide 2026"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/30"></div>
          </div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-white text-xs font-medium tracking-wider uppercase mb-4 whitespace-nowrap">
              Updated 2026
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-white max-w-3xl leading-tight">
              GTBuy Sheet: The Ultimate 2026 Spreadsheet Shopping Guide
            </h1>
          </div>
        </section>

        {/* Article Content */}
        <article className="w-full px-4 md:px-8 py-12 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            {/* Intro */}
            <p className="text-lg md:text-xl text-foreground-700 leading-relaxed mb-8">
              If you have spent any time in online streetwear or sneaker communities lately, you have probably heard people talking about something called a <strong>spreadsheet</strong>. Not the kind you use for accounting — a live, curated list of products, prices, and direct purchase links that makes online shopping dramatically faster and smarter. At the center of this growing trend is <strong>GTBuy Sheet</strong>, the gateway to one of the most comprehensive spreadsheet shopping platforms available today.
            </p>

            <p className="text-lg md:text-xl text-foreground-700 leading-relaxed mb-12">
              This guide covers everything: what a shopping spreadsheet actually is, how GTBuy Sheet connects you to thousands of verified items on kaspreadsheet.com, why the spreadsheet format beats traditional browsing, and how to navigate all 14 product categories to find exactly what you want. Whether you are new to spreadsheet shopping or a seasoned deal hunter, this is your complete walkthrough.
            </p>

            {/* Section 1: What Is GTBuy Sheet */}
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-16 mb-6">
              What Is GTBuy Sheet and How Does It Work?
            </h2>
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-5">
              GTBuy Sheet is not an online store in the traditional sense. Think of it as a highly organized <strong>directory and discovery engine</strong> for products that are already listed across various platforms. The core idea is simple: instead of jumping between a dozen different websites, search bars, and filter menus, you access one master spreadsheet that organizes everything into clean, sortable categories. Each entry includes the product name, the current price, a direct link to purchase, and often additional details like sizing availability or color options.
            </p>
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-5">
              The real power of <strong>GTBuy Sheet</strong> lies in its curation. The spreadsheet is updated weekly — sometimes daily during peak drop seasons — so you are always looking at fresh, verified inventory. Nothing sits on the list for months collecting digital dust. If an item sells out or a link breaks, it gets cleaned up in the next update. This commitment to accuracy is why users consistently rate the platform at 99% update accuracy.
            </p>
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-5">
              When you visit <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">GTBuy Sheet</Link>, you are not buying directly from this site. Instead, every category button and navigation link takes you to the corresponding section on <strong>kaspreadsheet.com</strong>, where the full spreadsheet lives. This gateway model keeps things lightweight and fast while giving you direct access to the most current data available.
            </p>

            {/* Section 2: Why Spreadsheet Shopping Beats Traditional Browsing */}
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-16 mb-6">
              Why Spreadsheet Shopping Is Better Than Traditional Browsing
            </h2>
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-5">
              Traditional online shopping follows a pattern: you open a website, scroll through pages of algorithm-curated results, deal with slow-loading image galleries, and hope the filters actually work the way you expect. Multiply that by five or six different sites and you have just burned thirty minutes without making any real progress. The spreadsheet model eliminates almost all of that friction.
            </p>
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-5">
              With <strong>GTBuy Sheet</strong> as your entry point, you arrive at kaspreadsheet.com and immediately see a structured table of products. You can sort by category — shoes, hoodies, jackets, accessories, and ten more. You can scan prices across dozens of items in seconds rather than waiting for individual product pages to render. You can compare similar products side by side without opening multiple tabs. The time savings alone make spreadsheet shopping worth trying, but the real advantage is discovery: you will consistently find items you never would have stumbled upon through normal browsing.
            </p>

            {/* Section 3: Category Breakdown */}
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-16 mb-6">
              Breaking Down All 14 Product Categories
            </h2>
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-5">
              One of the standout features of <strong>GTBuy Sheet</strong> is the breadth of coverage. The platform organizes products into 14 distinct categories, and understanding what each one contains helps you navigate more efficiently. Here is a quick tour of what you will find:
            </p>

            <div className="space-y-4 mt-6 mb-8">
              <div className="bg-background-50 border border-background-200/60 rounded-xl p-5">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                  <i className="ri-footprint-line w-5 h-5 inline-flex items-center justify-center text-primary-500 mr-2"></i>
                  Shoes
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  The flagship category. Sneakers, runners, slides, and everything in between. This is where the most frequent updates happen, with new drops appearing almost daily during release seasons.
                </p>
              </div>
              <div className="bg-background-50 border border-background-200/60 rounded-xl p-5">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                  <i className="ri-t-shirt-line w-5 h-5 inline-flex items-center justify-center text-primary-500 mr-2"></i>
                  Hoodies, T-Shirts &amp; Apparel
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  Covering hoodies, sweaters, t-shirts, jackets, pants, shorts, jerseys, and full matching sets. The apparel section is the largest combined category group on the platform, ideal for building complete outfits from a single spreadsheet view.
                </p>
              </div>
              <div className="bg-background-50 border border-background-200/60 rounded-xl p-5">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                  <i className="ri-shopping-bag-3-line w-5 h-5 inline-flex items-center justify-center text-primary-500 mr-2"></i>
                  Accessories, Bags &amp; More
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  Headwear, bags, belts, perfumes, underwear, and a general &ldquo;Other Stuff&rdquo; catch-all. These categories often contain the best hidden gems — items with lower competition that still offer great value.
                </p>
              </div>
            </div>

            <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-5">
              Every single one of these categories links directly to its dedicated section on kaspreadsheet.com, where you can view the full spreadsheet, apply filters, and start comparing products immediately. You can jump to any category from the navigation dropdown at the top of every page on <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">GTBuy Sheet</Link>.
            </p>

            {/* Section 4: How to Get Started */}
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-16 mb-6">
              How to Get Started with GTBuy Sheet in Under 60 Seconds
            </h2>
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-5">
              Getting started takes less than a minute. Here is the simplest path:
            </p>
            <ol className="list-decimal list-inside space-y-3 text-base md:text-lg text-foreground-700 leading-relaxed mb-5 ml-2">
              <li>From the <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">GTBuy Sheet homepage</Link>, click the &ldquo;Buy It Now&rdquo; button or select any category that interests you.</li>
              <li>You will land on <strong>kaspreadsheet.com</strong>, where the full spreadsheet loads with all current listings.</li>
              <li>Use the built-in category filters to narrow down to exactly what you are looking for.</li>
              <li>Click any product link to go directly to the purchase page on the original platform.</li>
              <li>Bookmark <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">GTBuy Sheet</Link> and check back weekly for fresh updates.</li>
            </ol>

            {/* Section 5: FAQ */}
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-16 mb-6">
              Frequently Asked Questions
            </h2>

            <div className="space-y-5">
              <div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                  Is GTBuy Sheet free to use?
                </h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  Yes. GTBuy Sheet is completely free. It serves as a gateway to kaspreadsheet.com, where the full spreadsheet is available. There are no membership fees, no paywalls, and no hidden costs.
                </p>
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                  How often is the spreadsheet updated?
                </h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  The spreadsheet on kaspreadsheet.com is refreshed on a weekly basis at minimum. During major release seasons and peak shopping periods, updates can happen multiple times per week. The platform maintains a 99% accuracy rate on all active listings.
                </p>
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                  Do I need an account to browse categories?
                </h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  No account is required to browse the 14 categories or access the spreadsheet on kaspreadsheet.com. Simply visit <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">GTBuy Sheet</Link>, pick a category, and start exploring.
                </p>
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                  What makes the GTBuy Sheet spreadsheet different from other shopping tools?
                </h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  Unlike algorithm-driven recommendation engines or cluttered marketplace interfaces, the spreadsheet format gives you raw, organized data that you control. You sort, you filter, you decide what matters. Combined with the 14-category coverage and weekly updates, it offers a level of transparency and efficiency that conventional shopping platforms simply cannot match.
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-16 text-center bg-background-50 border border-background-200/60 rounded-2xl p-8 md:p-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                Ready to Start Shopping Smarter?
              </h2>
              <p className="text-foreground-600 text-sm md:text-base mb-8 max-w-md mx-auto">
                Jump into the full spreadsheet now and discover why thousands of shoppers trust GTBuy Sheet as their go-to shopping gateway.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-8 py-3.5 rounded-full text-sm md:text-base font-semibold bg-foreground-900 text-background-50 hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
                >
                  Buy It Now
                </a>
                <Link
                  to="/"
                  className="px-8 py-3.5 rounded-full text-sm md:text-base font-semibold border-2 border-primary-400 text-primary-600 hover:bg-primary-50 transition-all whitespace-nowrap"
                >
                  Back to GTBuy Sheet
                </Link>
              </div>
            </div>

            {/* Breadcrumb / Internal Link */}
            <nav className="mt-10 pt-8 border-t border-background-200/60" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
                <li>
                  <Link to="/" className="hover:text-primary-600 transition-colors font-medium">
                    GTBuy Sheet Home
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                </li>
                <li>
                  <span className="text-foreground-700">Ultimate 2026 Spreadsheet Shopping Guide</span>
                </li>
              </ol>
            </nav>
          </div>
        </article>

        {/* Schema.org structured data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: 'GTBuy Sheet: The Ultimate 2026 Spreadsheet Shopping Guide',
              description:
                'Complete guide to GTBuy Sheet and spreadsheet shopping. Learn how the platform works, explore all 14 categories, and discover why spreadsheet shopping beats traditional browsing.',
              author: { '@type': 'Organization', name: 'GTBuy Sheet' },
              publisher: { '@type': 'Organization', name: 'GTBuy Sheet', url: import.meta.env.VITE_SITE_URL },
              datePublished: '2026-06-15',
              dateModified: '2026-07-01',
              mainEntityOfPage: { '@type': 'WebPage', '@id': `${import.meta.env.VITE_SITE_URL}/guide/gtbuy-sheet-ultimate-guide-2026` },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: 'GTBuy Sheet Home',
                  item: import.meta.env.VITE_SITE_URL,
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: 'Ultimate 2026 Spreadsheet Shopping Guide',
                  item: `${import.meta.env.VITE_SITE_URL}/guide/gtbuy-sheet-ultimate-guide-2026`,
                },
              ],
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: [
                {
                  '@type': 'Question',
                  name: 'Is GTBuy Sheet free to use?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Yes. GTBuy Sheet is completely free. It serves as a gateway to kaspreadsheet.com, where the full spreadsheet is available. There are no membership fees, no paywalls, and no hidden costs.',
                  },
                },
                {
                  '@type': 'Question',
                  name: 'How often is the spreadsheet updated?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'The spreadsheet on kaspreadsheet.com is refreshed on a weekly basis at minimum. During major release seasons and peak shopping periods, updates can happen multiple times per week. The platform maintains a 99% accuracy rate on all active listings.',
                  },
                },
                {
                  '@type': 'Question',
                  name: 'Do I need an account to browse categories?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'No account is required to browse the 14 categories or access the spreadsheet on kaspreadsheet.com. Simply visit GTBuy Sheet, pick a category, and start exploring.',
                  },
                },
                {
                  '@type': 'Question',
                  name: 'What makes the GTBuy Sheet spreadsheet different from other shopping tools?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Unlike algorithm-driven recommendation engines or cluttered marketplace interfaces, the spreadsheet format gives you raw, organized data that you control. Combined with the 14-category coverage and weekly updates, it offers a level of transparency and efficiency that conventional shopping platforms cannot match.',
                  },
                },
              ],
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}